import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EntitySocialLinkIcons } from "@/components/EntitySocialLinkIcons";
import { ImageLightbox } from "@/components/ImageLightbox";
import { supabase } from "@/lib/supabase";
import { batchHasPublicDetailPage, batchPublicPrimaryName } from "@/lib/batchDisplay";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import {
  normalizeExternalHref,
  type EntitySocialLinksFields,
} from "@/lib/entitySocialLinks";

type Batch = {
  logo_url: string | null;
  official_title: string | null;
  custom_title: string | null;
  ol_year: number | null;
  al_year: number | null;
  description: string | null;
  exco_photo_url: string | null;
} & EntitySocialLinksFields;

type Project = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type NewsItem = {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type PageState =
  | "loading"
  | "ok"
  | "not_found"
  | "unpublished";

export function BatchDetailPage() {
  const { slug } = useParams();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [batch, setBatch] = useState<Batch | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    if (!slug) return;
    setPageState("loading");
    setBatch(null);
    setProjects([]);
    setNews([]);
    void supabase
      .from("batches")
      .select(
        "id,logo_url,official_title,custom_title,ol_year,al_year,description,exco_photo_url,social_facebook_url,social_instagram_url,social_youtube_url,social_website_url,social_other_url"
      )
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setPageState("not_found");
          return;
        }
        const row = data as { id: string; al_year: number | null } & Batch;
        const cutoff = maxPublicBatchAlYear();
        if (row.al_year != null && row.al_year > cutoff) {
          setPageState("not_found");
          return;
        }
        const { id, ...rest } = row;
        setBatch(rest);
        void Promise.all([
          supabase
            .from("projects")
            .select("id,title,description,image_url,link_url,created_at,updated_at")
            .eq("batch_id", id)
            .order("updated_at", { ascending: false })
            .then(({ data: p }) => (p as Project[]) ?? []),
          supabase
            .from("news")
            .select("id,slug,title,description,image_url,published_at,created_at,updated_at")
            .eq("batch_id", id)
            .order("updated_at", { ascending: false })
            .then(({ data: n }) => (n as NewsItem[]) ?? []),
        ]).then(([p, n]) => {
          setProjects(p);
          setNews(n);
          const eligible = batchHasPublicDetailPage({
            exco_photo_url: rest.exco_photo_url,
            projects: [{ count: p.length }],
            news: [{ count: n.length }],
          });
          setPageState(eligible ? "ok" : "unpublished");
        });
      });
  }, [slug]);

  if (!slug) return null;

  if (pageState === "not_found") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-brand-blue/70">
        Batch not found.
        <div className="mt-6">
          <Link to="/alumni/batches" className="text-brand-maroon font-semibold">
            ← All batches
          </Link>
        </div>
      </div>
    );
  }

  if (pageState === "unpublished" && batch) {
    const title = batchPublicPrimaryName(batch);
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-brand-blue/85">
        <p className="text-lg font-semibold text-brand-blue">{title}</p>
        <p className="mt-4 max-w-md mx-auto">
          This batch does not have a public page yet. A page is published when the
          batch adds at least one project, a news post, or an executive committee
          photo.
        </p>
        <div className="mt-8">
          <Link to="/alumni/batches" className="text-brand-maroon font-semibold">
            ← All batches
          </Link>
        </div>
      </div>
    );
  }

  const title = batch ? batchPublicPrimaryName(batch) : "Batch";

  return (
    <div className="mx-auto max-w-7xl px-4 py-14">
      <Link
        to="/alumni/batches"
        className="text-sm font-semibold text-brand-maroon hover:underline"
      >
        ← Batches
      </Link>
      {pageState === "loading" ? (
        <p className="mt-8 text-brand-blue/60">Loading…</p>
      ) : batch ? (
        <>
          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-start">
            {batch.logo_url && (
              <img
                src={batch.logo_url}
                alt=""
                className="h-28 w-28 rounded-xl border border-brand-gray bg-white object-contain p-2"
              />
            )}
            <div>
              <h1 className="font-display text-4xl font-bold text-brand-blue">
                {title}
              </h1>
              {batch.custom_title?.trim() ? (
                <p className="mt-2 text-brand-blue/80">{batch.custom_title.trim()}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-brand-blue/70">
                {batch.ol_year != null && <span>O/L {batch.ol_year}</span>}
                {batch.al_year != null && <span>A/L {batch.al_year}</span>}
              </div>
              <EntitySocialLinkIcons links={batch} className="mt-4" />
            </div>
          </div>
          {batch.exco_photo_url && (
            <div className="mt-8 max-w-5xl">
              <ImageLightbox
                src={batch.exco_photo_url}
                thumbMaxHeightClass="max-h-64 sm:max-h-80 md:max-h-96"
                hint="Click to enlarge"
              />
            </div>
          )}
          {batch.description && (
            <p className="mt-8 whitespace-pre-line text-brand-blue/90 leading-relaxed">
              {batch.description}
            </p>
          )}
        </>
      ) : null}

      {pageState === "ok" && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold text-brand-blue">
            Projects
          </h2>
          {projects.length === 0 ? (
            <p className="mt-4 text-sm text-brand-blue/60">No projects listed.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((p) => {
                const projectMonth = formatNewsMonthYear(p.updated_at ?? p.created_at);
                const safeLink = normalizeExternalHref(p.link_url);
                return (
                  <li
                    key={p.id}
                    className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-gray bg-brand-gray/30"
                  >
                    <Link
                      to={`/projects/${p.id}`}
                      className="flex min-h-0 flex-1 flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon focus-visible:ring-offset-2"
                      aria-label={p.title ? `View project: ${p.title}` : "View project"}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          loading="lazy"
                          className="h-44 w-full shrink-0 object-cover"
                        />
                      ) : null}
                      <div className="flex min-h-0 flex-1 flex-col p-5">
                        {projectMonth ? (
                          <p className="text-xs font-semibold uppercase tracking-wide text-brand-maroon">
                            {projectMonth}
                          </p>
                        ) : null}
                        <h3 className="mt-1 font-display text-xl font-bold text-brand-blue">{p.title}</h3>
                        {p.description ? (
                          <p className="mt-2 line-clamp-4 text-sm text-brand-blue/85 whitespace-pre-line">
                            {p.description}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                    {safeLink ? (
                      <div className="border-t border-brand-gray/40 px-5 py-3">
                        <a
                          href={safeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-brand-maroon hover:underline"
                        >
                          Open external link →
                        </a>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {pageState === "ok" && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold text-brand-blue">
            News
          </h2>
          {news.length === 0 ? (
            <p className="mt-4 text-sm text-brand-blue/60">No news listed.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {news.map((n) => {
                const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
                const detailHref = n.slug ? `/news/other/${n.slug}` : "/news/other";
                return (
                  <li key={n.id} className="h-full">
                    <Link
                      to={detailHref}
                      className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-gray bg-white shadow-sm transition hover:border-brand-maroon/35 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-maroon"
                      aria-label={n.title ? `Read article: ${n.title}` : "Read article"}
                    >
                      <div className="aspect-[16/9] shrink-0 bg-brand-gray">
                        {n.image_url ? (
                          <img
                            src={n.image_url}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        {dateLine ? (
                          <time
                            dateTime={n.updated_at ?? n.created_at ?? undefined}
                            className="text-xs font-semibold uppercase tracking-wide text-brand-maroon"
                          >
                            {dateLine}
                          </time>
                        ) : null}
                        <h3 className="mt-2 font-display text-xl font-bold text-brand-blue">
                          {n.title}
                        </h3>
                        {n.description ? (
                          <p className="mt-2 line-clamp-4 text-sm text-brand-blue/85 whitespace-pre-line">
                            {n.description}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
